import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Award, Star, Clock, ArrowRight, Linkedin, 
  Twitter, Mail, Globe, Target, Shield, TrendingUp,
  CheckCircle, Zap, Building2, Activity
} from 'lucide-react';
import Layout from '../../components/layout/Layout';

const TeamPage = () => {
  const leadership = [
    {
      name: "Sarah Johnson",
      role: "Chief Executive Officer",
      bio: "Visionary leader with 15+ years of experience in renewable energy and technology innovation",
      expertise: ["Strategic Leadership", "Business Development", "Technology Innovation"],
      experience: "15+ years",
      education: "MBA, Stanford University",
      image: "/assets/img/team/ceo.jpg",
      gradient: "from-blue-600 to-blue-700",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "sarah.johnson@axonmetric.com"
      }
    },
    {
      name: "Michael Chen",
      role: "Chief Technology Officer",
      bio: "Technology expert specializing in AI, machine learning, and renewable energy systems",
      expertise: ["AI & Machine Learning", "System Architecture", "Data Analytics"],
      experience: "12+ years",
      education: "PhD Computer Science, MIT",
      image: "/assets/img/team/cto.jpg",
      gradient: "from-gray-600 to-gray-700",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "michael.chen@axonmetric.com"
      }
    },
    {
      name: "Emily Rodriguez",
      role: "Head of Operations",
      bio: "Operations specialist with deep expertise in renewable energy project management",
      expertise: ["Project Management", "Operations Optimization", "Team Leadership"],
      experience: "10+ years",
      education: "MSc Engineering, UC Berkeley",
      image: "/assets/img/team/operations.jpg",
      gradient: "from-blue-500 to-blue-600",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "emily.rodriguez@axonmetric.com"
      }
    },
    {
      name: "David Kim",
      role: "Lead Data Scientist",
      bio: "Data science expert focused on predictive analytics and renewable energy optimization",
      expertise: ["Predictive Analytics", "Statistical Modeling", "Energy Optimization"],
      experience: "8+ years",
      education: "PhD Statistics, Harvard University",
      image: "/assets/img/team/data-scientist.jpg",
      gradient: "from-gray-500 to-gray-600",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "david.kim@axonmetric.com"
      }
    }
  ];

  const departments = [
    {
      name: "Engineering",
      members: 25,
      description: "Core engineering team developing innovative renewable energy solutions",
      icon: Activity,
      gradient: "from-blue-600 to-blue-700"
    },
    {
      name: "Data Science",
      members: 18,
      description: "AI and analytics experts driving intelligent energy optimization",
      icon: TrendingUp,
      gradient: "from-gray-600 to-gray-700"
    },
    {
      name: "Operations",
      members: 32,
      description: "Field operations and project management specialists",
      icon: Shield,
      gradient: "from-blue-500 to-blue-600"
    },
    {
      name: "Business Development",
      members: 15,
      description: "Strategic partnerships and business growth experts",
      icon: Target,
      gradient: "from-gray-500 to-gray-600"
    }
  ];

  const values = [
    {
      icon: Target,
      title: "Innovation Excellence",
      description: "Pioneering cutting-edge solutions in renewable energy technology",
      gradient: "from-blue-600 to-blue-700"
    },
    {
      icon: Users,
      title: "Collaborative Culture",
      description: "Fostering teamwork and knowledge sharing across all departments",
      gradient: "from-gray-600 to-gray-700"
    },
    {
      icon: Shield,
      title: "Integrity & Trust",
      description: "Building lasting relationships through transparency and reliability",
      gradient: "from-blue-500 to-blue-600"
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Committed to accelerating the world's transition to sustainable energy",
      gradient: "from-gray-500 to-gray-600"
    }
  ];

  const stats = [
    { value: "90+", label: "Team Members", icon: Users },
    { value: "15+", label: "Years Experience", icon: Clock },
    { value: "25+", label: "Countries Served", icon: Globe },
    { value: "100+", label: "Projects Completed", icon: CheckCircle }
  ];

  return (
    <Layout header={6} footer={6}>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50 pt-[50px]">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-full text-sm font-bold mb-6"
            >
              Our Team
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl lg:text-7xl font-bold bg-gradient-to-r from-gray-800 via-blue-700 to-gray-800 bg-clip-text text-transparent mb-8"
            >
              Meet the Experts
              <br />
                <span className="bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                Behind Our Success
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-gray-600 leading-relaxed mb-12 max-w-3xl mx-auto"
            >
              Our diverse team of experts brings together decades of combined experience in renewable energy, 
              technology, and innovation to drive sustainable solutions for the future.
            </motion.p>

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="text-white" size={32} />
                  </div>
                  <div className="text-3xl font-bold text-gray-800 mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 bg-white">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-4"
            >
              Leadership Team
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-800 via-blue-800 to-gray-800 bg-clip-text text-transparent mb-6"
            >
              Visionary Leaders
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Our executive team brings together decades of experience in renewable energy, technology, and business leadership
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {leadership.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 hover:bg-white/90 transition-all duration-500 hover:scale-105 shadow-xl hover:shadow-2xl relative overflow-hidden">
                  {/* Profile Header */}
                  <div className="flex items-start gap-6 mb-6">
                    <div className={`w-24 h-24 bg-gradient-to-r ${member.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      <Users className="text-white" size={48} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-gray-900 transition-colors duration-300">
                        {member.name}
                      </h3>
                      <p className="text-lg font-semibold text-gray-600 mb-2">{member.role}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {member.experience}
                        </span>
                        <span className="flex items-center gap-1">
                          <Award className="w-4 h-4" />
                          {member.education}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bio */}
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {member.bio}
                  </p>
                  
                  {/* Expertise */}
                  <div className="mb-6">
                    <div className="text-sm font-semibold text-gray-700 mb-3">Areas of Expertise:</div>
                    <div className="flex flex-wrap gap-2">
                      {member.expertise.map((skill, skillIndex) => (
                        <span key={skillIndex} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Social Links */}
                  <div className="flex items-center gap-4">
                    <a
                      href={member.social.linkedin}
                      className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                    <a
                      href={member.social.twitter}
                      className="w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                    <a
                      href={`mailto:${member.social.email}`}
                      className="w-10 h-10 bg-gradient-to-r from-gray-500 to-gray-600 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300"
                    >
                      <Mail className="w-5 h-5" />
                    </a>
                  </div>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%]"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Departments Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-4"
            >
              Our Departments
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-800 via-green-800 to-gray-800 bg-clip-text text-transparent mb-6"
            >
              Specialized Teams
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Our cross-functional teams work together to deliver innovative renewable energy solutions
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {departments.map((dept, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 text-center hover:bg-white/90 transition-all duration-500 hover:scale-105 shadow-xl hover:shadow-2xl relative overflow-hidden">
                  {/* Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-r ${dept.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <dept.icon className="text-white" size={32} />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-gray-900 transition-colors duration-300">
                    {dept.name}
                  </h3>
                  <div className="text-2xl font-bold text-gray-700 mb-2">{dept.members}</div>
                  <div className="text-sm text-gray-500 mb-4">Team Members</div>
                  <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {dept.description}
                  </p>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%]"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-4"
            >
              Our Values
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-800 via-orange-800 to-gray-800 bg-clip-text text-transparent mb-6"
            >
              What Drives Us
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Our core values shape our culture and guide everything we do
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 text-center hover:bg-white/90 transition-all duration-500 hover:scale-105 shadow-xl hover:shadow-2xl relative overflow-hidden h-64 w-full flex flex-col">
                  {/* Icon */}
                  <div className={`w-12 h-12 bg-gradient-to-r ${value.gradient} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                    <value.icon className="text-white" size={24} />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 group-hover:text-gray-900 transition-colors duration-300 overflow-hidden" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4',
                      maxHeight: '2.8rem'
                    }}>
                      {value.title}
                    </h3>
                    <p className="text-sm leading-relaxed group-hover:text-gray-700 transition-colors duration-300 flex-1 overflow-hidden" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4',
                      maxHeight: '4.2rem'
                    }}>
                      {value.description}
                    </p>
                  </div>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%]"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-5xl font-bold text-white mb-6"
            >
              Join Our Team
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-gray-300 mb-8 leading-relaxed"
            >
              Be part of a team that's shaping the future of renewable energy. We're always looking for talented individuals who share our passion for innovation and sustainability.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href="/careers"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-4 px-8 rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 text-lg"
              >
                <span>View Open Positions</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-bold py-4 px-8 rounded-2xl hover:bg-white/20 transition-all duration-300 transform hover:scale-105 text-lg"
              >
                <span>Contact HR</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default TeamPage;
